// src/components/AppointmentTypes.js

import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  deleteDoc, 
  doc, 
  updateDoc 
} from 'firebase/firestore';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Alert,
} from '@mui/material';

// TenantContext importieren, um tenantId zu erhalten
import { useTenant } from '../../contexts/TenantContext';

const AppointmentTypes = () => {
  // State
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState({ name: '', duration: '' });
  const [editTypeId, setEditTypeId] = useState(null);
  const [editTypeData, setEditTypeData] = useState({ name: '', duration: '' });
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Tenant aus Context holen
  const { tenantId } = useTenant();

  // 1) Terminarten laden
  useEffect(() => {
    if (!tenantId) return; // Falls tenantId noch nicht geladen
    
    const typesRef = collection(db, 'tenants', tenantId, 'appointmentTypes');
  
    // Echtzeit-Listener
    const unsubscribe = onSnapshot(typesRef, (snapshot) => {
      const typesList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name,
          duration: Number(data.duration),
          assignedEmployees: data.assignedEmployees || []
        };
      });
      setTypes(typesList);
    }, (error) => {
      console.error("Fehler beim Abrufen der Terminarten:", error);
      setError('Fehler beim Abrufen der Terminarten.');
    });
  
    // Cleanup: Listener entfernen, wenn Komponente unmountet wird
    return () => unsubscribe();
  }, [tenantId]);
  

  // 2) Terminart hinzufügen
  const handleAddType = async () => {
    setError(null);
    setMessage(null);

    if (!newType.name || !newType.duration) {
      setError('Alle Felder sind erforderlich.');
      return;
    }

    try {
      // Pfad zum Schreiben
      const typesRef = collection(db, 'tenants', tenantId, 'appointmentTypes');
      const docRef = await addDoc(typesRef, {
        name: newType.name,
        duration: parseInt(newType.duration, 10),
        assignedEmployees: []
      });

      // Im State aktualisieren
      setTypes([
        ...types,
        {
          id: docRef.id,
          name: newType.name,
          duration: parseInt(newType.duration, 10),
          assignedEmployees: []
        }
      ]);

      // Formular zurücksetzen
      setNewType({ name: '', duration: '' });
      setMessage('Terminart erfolgreich hinzugefügt.');
    } catch (err) {
      console.error(err);
      setError('Fehler beim Hinzufügen der Terminart.');
    }
  };

  // 3) Terminart löschen
  const handleDeleteType = async (id) => {
    setError(null);
    setMessage(null);

    try {
      // Pfad zum Löschen: tenants/{tenantId}/appointmentTypes/{id}
      await deleteDoc(doc(db, 'tenants', tenantId, 'appointmentTypes', id));
      setTypes(types.filter(type => type.id !== id));
      setMessage('Terminart erfolgreich gelöscht.');
    } catch (err) {
      console.error(err);
      setError('Fehler beim Löschen der Terminart.');
    }
  };

  // 4) Terminart bearbeiten (in Form laden)
  const handleEditType = (type) => {
    setEditTypeId(type.id);
    setEditTypeData({
      name: type.name,
      duration: String(type.duration),  // oder direkt type.duration
    });
  };

  // 5) Terminart aktualisieren
  const handleUpdateType = async () => {
    setError(null);
    setMessage(null);

    if (!editTypeData.name || !editTypeData.duration) {
      setError('Alle Felder sind erforderlich.');
      return;
    }

    try {
      // KORREKT: tenants/{tenantId}/appointmentTypes/{editTypeId}
      const typeDocRef = doc(db, 'tenants', tenantId, 'appointmentTypes', editTypeId);
      await updateDoc(typeDocRef, {
        name: editTypeData.name,
        duration: parseInt(editTypeData.duration, 10),
      });

      // State updaten
      setTypes(types.map(type =>
        type.id === editTypeId
          ? { ...type, ...editTypeData, duration: parseInt(editTypeData.duration, 10) }
          : type
      ));

      // Formular zurücksetzen
      setEditTypeId(null);
      setEditTypeData({ name: '', duration: '' });
      setMessage('Terminart erfolgreich aktualisiert.');
    } catch (err) {
      console.error(err);
      setError('Fehler beim Aktualisieren der Terminart.');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      {/* Formular: Neue Terminart */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Neue Terminart hinzufügen
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Name"
            variant="outlined"
            value={newType.name}
            onChange={(e) => setNewType({ ...newType, name: e.target.value })}
          />
          <TextField
            label="Dauer (Minuten)"
            type="number"
            variant="outlined"
            value={newType.duration}
            onChange={(e) => setNewType({ ...newType, duration: e.target.value })}
          />
          <Button variant="contained" color="primary" onClick={handleAddType}>
            Hinzufügen
          </Button>
        </Box>
      </Box>

      {/* Liste: Terminarten */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Dauer (Minuten)</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id}>
                <TableCell>
                  {editTypeId === type.id ? (
                    <TextField
                      variant="outlined"
                      value={editTypeData.name}
                      onChange={(e) => setEditTypeData({ ...editTypeData, name: e.target.value })}
                    />
                  ) : (
                    type.name
                  )}
                </TableCell>

                <TableCell>
                  {editTypeId === type.id ? (
                    <TextField
                      type="number"
                      variant="outlined"
                      value={editTypeData.duration}
                      onChange={(e) => setEditTypeData({ ...editTypeData, duration: e.target.value })}
                    />
                  ) : (
                    type.duration
                  )}
                </TableCell>

                <TableCell>
            {editTypeId === type.id ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleUpdateType}
                  sx={{ mr: 1 }} // Abstand nach rechts
                >
                  Speichern
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => setEditTypeId(null)}
                >
                  Abbrechen
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleEditType(type)}
                  sx={{ mr: 1 }} // Abstand nach rechts
                >
                  Bearbeiten
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteType(type.id)}
                >
                  Löschen
                </Button>
              </>
            )}
</TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AppointmentTypes;
